export interface ImageValidationResult {
  valid: boolean;
  error?: string;
  size?: number;
  type?: string;
}

export interface CompressImageOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  maxSizeKB?: number;
}

export const validateImageFile = (file: File): ImageValidationResult => {
  const MAX_SIZE_MB = 2;
  const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

  if (!file) {
    return {
      valid: false,
      error: 'No se seleccionó ningún archivo'
    };
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    return {
      valid: false,
      error: 'Formato de imagen no válido. Usa JPG, PNG, GIF o WebP'
    };
  }

  if (file.size > MAX_SIZE_BYTES) {
    return {
      valid: false,
      error: `La imagen es muy grande. Tamaño máximo: ${MAX_SIZE_MB}MB`
    };
  }

  return {
    valid: true,
    size: file.size,
    type: file.type
  };
};

export const compressImage = (
  file: File,
  options: CompressImageOptions = {}
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const {
      maxWidth = 400,
      maxHeight = 400,
      quality = 0.8,
      maxSizeKB = 200
    } = options;

    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('No se pudo obtener el contexto del canvas'));
          return;
        }

        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        let currentQuality = quality;
        let compressed = canvas.toDataURL('image/jpeg', currentQuality);

        const getBase64Size = (base64: string): number => {
          const base64Length = base64.length - (base64.indexOf(',') + 1);
          return (base64Length * 3) / 4;
        };

        while (getBase64Size(compressed) / 1024 > maxSizeKB && currentQuality > 0.1) {
          currentQuality -= 0.1;
          compressed = canvas.toDataURL('image/jpeg', currentQuality);
        }

        console.log('📷 Imagen comprimida:', {
          originalSize: file.size,
          compressedSize: getBase64Size(compressed),
          quality: currentQuality,
          dimensions: `${width}x${height}`
        });

        resolve(compressed);
      };

      img.onerror = () => {
        reject(new Error('Error al cargar la imagen'));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Error al leer el archivo'));
    };

    reader.readAsDataURL(file);
  });
};

export const getImageDimensions = (base64: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      resolve({
        width: img.width,
        height: img.height
      });
    };

    img.onerror = () => {
      reject(new Error('Error al cargar la imagen'));
    };

    img.src = base64;
  });
};

export const createImageThumbnail = (base64: string, size: number = 100): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('No se pudo obtener el contexto del canvas'));
        return;
      }

      const aspectRatio = img.width / img.height;
      let drawWidth = size;
      let drawHeight = size;
      let offsetX = 0;
      let offsetY = 0;

      if (aspectRatio > 1) {
        drawWidth = size * aspectRatio;
        offsetX = -(drawWidth - size) / 2;
      } else {
        drawHeight = size / aspectRatio;
        offsetY = -(drawHeight - size) / 2;
      }

      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      resolve(canvas.toDataURL('image/jpeg', 0.9));
    };

    img.onerror = () => {
      reject(new Error('Error al cargar la imagen'));
    };

    img.src = base64;
  });
};
